import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Check, Loader2, Plus, Search, FolderOpen, Globe, Lock } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useAddToCollection,
  useRemoveFromCollection,
  useCreateCollection,
  useCollectionsForStash,
  useCollectionsForEntity,
  useWritableCollections,
} from "@/hooks/useCollections";
import type { CollectionItemType, Collection } from "@/types/collections";
import { UserAvatar } from "@/components/shared/UserAvatar";
import { EntityConfirmationStep } from "@/components/collections/EntityConfirmationStep";
import { PrivacyWarningDialog } from "@/components/collections/PrivacyWarningDialog";
import { useBatchStashEntities } from "@/hooks/useBatchStashEntities";
import { useEntityConfirmationFlow } from "@/hooks/useEntityConfirmationFlow";
import { buildAddSuccessLabel } from "@/utils/entityMappers";
import { CAPTION, META, ROW_TITLE } from "@/lib/typography";

interface AddToCollectionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: Array<{ type: CollectionItemType; id: string }>;
  /** Called after a successful add when in batch mode (items.length > 1). */
  onBatchComplete?: () => void;
  /** When "private", shows a warning next to public collections. */
  itemVisibility?: "private" | "public";
}

export function AddToCollectionSheet({
  open,
  onOpenChange,
  items,
  onBatchComplete,
  itemVisibility,
}: AddToCollectionSheetProps) {
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [pendingCollection, setPendingCollection] = useState<Collection | null>(null);

  // Privacy warning state
  const [privacyWarningCollection, setPrivacyWarningCollection] = useState<Collection | null>(null);
  const [loadingCollectionId, setLoadingCollectionId] = useState<string | null>(null);

  const { collections } = useWritableCollections();
  const addMutation = useAddToCollection();
  const removeMutation = useRemoveFromCollection();
  const createMutation = useCreateCollection();

  // Reverse lookups to know which collections already contain the item(s).
  const isBatch = items.length > 1;
  const firstItem = items[0];
  const isStash = firstItem?.type === "stash";
  const { data: stashCollections } = useCollectionsForStash(
    !isBatch && isStash ? firstItem?.id : undefined,
  );
  const { data: entityCollections } = useCollectionsForEntity(
    !isBatch && !isStash ? firstItem?.id : undefined,
  );

  // Fetch + deduplicate entities for all stash-type items
  const stashIds = useMemo(
    () => items.filter((i) => i.type === "stash").map((i) => i.id),
    [items],
  );
  const { entities: relatedEntities, hasEntities: hasRelatedEntities } =
    useBatchStashEntities(stashIds, open);

  // Fire the actual add mutation with optional entity items
  const fireAdd = useCallback(
    (collection: Collection, includeEntities: boolean, entityIds: Set<string>) => {
      const entityItems: Array<{ type: CollectionItemType; id: string }> =
        includeEntities
          ? Array.from(entityIds).map((id) => ({ type: "entity" as const, id }))
          : [];
      const combinedItems = [...items, ...entityItems];
      setLoadingCollectionId(collection.collection_id);

      addMutation.mutate(
        { collectionId: collection.collection_id, items: combinedItems },
        {
          onSuccess: () => {
            setLoadingCollectionId(null);
            if (isBatch || entityItems.length > 0) {
              toast.success(buildAddSuccessLabel({
                stashCount: items.length,
                entityCount: entityItems.length,
                collectionTitle: collection.title,
              }));
              onOpenChange(false);
              onBatchComplete?.();
            }
          },
          onError: () => setLoadingCollectionId(null),
        },
      );
    },
    [items, isBatch, addMutation, onBatchComplete, onOpenChange],
  );

  // Two-step entity confirmation flow
  const confirmationFlow = useEntityConfirmationFlow({
    entities: relatedEntities,
    hasEntities: hasRelatedEntities,
    onAdd: (includeEntities, entityIds) => {
      if (!pendingCollection) return;
      fireAdd(pendingCollection, includeEntities, entityIds);
    },
  });

  const containingCollectionIds = useMemo(() => {
    const source = isStash ? stashCollections : entityCollections;
    const ids = new Set<string>();
    for (const c of source?.items ?? []) {
      ids.add(c.collection_id);
    }
    return ids;
  }, [isStash, stashCollections, entityCollections]);

  const filtered = useMemo(() => {
    if (!search.trim()) return collections;
    const q = search.toLowerCase();
    return collections.filter((c) => c.title.toLowerCase().includes(q));
  }, [collections, search]);

  // Reset all local state when the drawer transitions from open → closed.
  // Covers user-initiated close (drag / X) and programmatic close (after add).
  const resetRef = useRef(confirmationFlow.reset);
  resetRef.current = confirmationFlow.reset;
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open) {
      resetRef.current();
      setPendingCollection(null);
      setPrivacyWarningCollection(null);
      setLoadingCollectionId(null);
      setSearch("");
      setCreating(false);
      setNewTitle("");
    }
    wasOpen.current = open;
  }, [open]);

  const proceedWithAdd = useCallback(
    (collection: Collection) => {
      setPendingCollection(collection);
      // Always enter confirmation — the hook auto-skips if no entities exist
      // after fetch completes. This avoids skipping when entities are still loading.
      confirmationFlow.startConfirmation();
    },
    [confirmationFlow],
  );

  const handleToggle = useCallback(
    (collection: Collection) => {
      const isIn = containingCollectionIds.has(collection.collection_id);
      if (isIn) {
        const itemKeys = items.map((i) => `${i.type}#${i.id}`);
        setLoadingCollectionId(collection.collection_id);
        removeMutation.mutate(
          { collectionId: collection.collection_id, itemKeys },
          {
            onSuccess: () => setLoadingCollectionId(null),
            onError: () => setLoadingCollectionId(null),
          },
        );
      } else if (itemVisibility === "private" && collection.visibility === "public") {
        setPrivacyWarningCollection(collection);
      } else {
        proceedWithAdd(collection);
      }
    },
    [items, itemVisibility, containingCollectionIds, removeMutation, proceedWithAdd],
  );

  const handleCreate = useCallback(() => {
    const title = newTitle.trim();
    if (!title) return;
    createMutation.mutate(
      { title },
      {
        onSuccess: (created) => {
          setCreating(false);
          setNewTitle("");

          setPendingCollection(created);
          confirmationFlow.startConfirmation();
        },
      },
    );
  }, [newTitle, createMutation, confirmationFlow]);

  return (
    <>
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerTitle className="sr-only">
          {isBatch ? `Save ${items.length} items to collection` : "Save to collection"}
        </DrawerTitle>

        {confirmationFlow.step === "confirm" && pendingCollection ? (
          <EntityConfirmationStep
            entities={relatedEntities}
            selectedEntityIds={confirmationFlow.selectedEntityIds}
            onToggleEntity={confirmationFlow.toggleEntity}
            onToggleAll={confirmationFlow.toggleAll}
            onToggleGroup={confirmationFlow.toggleGroup}
            stashCount={items.length}
            collectionTitle={pendingCollection.title}
            onAdd={confirmationFlow.confirmAdd}
            onBack={() => { confirmationFlow.goBack(); setPendingCollection(null); }}
            isPending={addMutation.isPending}
          />
        ) : (
          <>
            {isBatch && (
              <p className={cn(META, "mb-2")}>
                Adding {items.length} {items.length === 1 ? "item" : "items"}
              </p>
            )}

            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search collections..."
                className="pl-10"
              />
            </div>

            {/* Create new */}
            {creating ? (
              <div className="mb-3 rounded-xl border border-border p-3 space-y-3">
                <Input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Collection name"
                  maxLength={100}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreate();
                    if (e.key === "Escape") setCreating(false);
                  }}
                />
                <div className="flex gap-3">
                  <Button
                    variant="secondary"
                    size="modal"
                    onClick={() => setCreating(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="gradient"
                    size="modal"
                    onClick={handleCreate}
                    disabled={!newTitle.trim() || createMutation.isPending}
                    className="flex-1"
                  >
                    {createMutation.isPending ? "Creating..." : "Create & Add"}
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setCreating(true)}
                className={cn(META, "mb-3 flex w-full items-center gap-3 rounded-xl border-2 border-dashed border-border p-3 text-left transition-colors hover:border-primary hover:text-primary")}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <Plus className="h-5 w-5" />
                </div>
                <span className="font-medium">New collection</span>
              </button>
            )}

            {/* Collection list */}
            <div className="flex flex-col gap-1 overflow-y-auto pb-safe">
              {filtered.length === 0 && (
                <div className={cn(META, "flex flex-col items-center gap-2 py-8 text-center")}>
                  <FolderOpen className="h-8 w-8" />
                  <span>
                    {search.trim() ? "No collections match your search" : "No collections yet"}
                  </span>
                </div>
              )}
              {filtered.map((collection) => {
                const isIn = containingCollectionIds.has(collection.collection_id);
                const thumbnails = collection.cover_thumbnails ?? [];
                const members = collection.members ?? [];
                return (
                  <button
                    key={collection.collection_id}
                    type="button"
                    onClick={() => handleToggle(collection)}
                    className="flex items-center gap-3 rounded-xl p-3 text-left transition-colors hover:bg-accent/50 active:bg-accent"
                  >
                    {/* Thumbnail or fallback */}
                    {thumbnails.length > 0 ? (
                      <span className="h-10 w-10 shrink-0 overflow-hidden rounded-lg">
                        <img src={thumbnails[0]} alt="" className="h-full w-full object-cover" />
                      </span>
                    ) : (
                      <span
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                        style={{ backgroundColor: `${collection.color}20` }}
                      >
                        <FolderOpen className="h-5 w-5" style={{ color: collection.color }} />
                      </span>
                    )}

                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className={cn(ROW_TITLE, "truncate")}>{collection.title}</span>
                        {collection.visibility === "private" ? (
                          <Lock className="h-3 w-3 shrink-0 text-muted-foreground" />
                        ) : (
                          <Globe className="h-3 w-3 shrink-0 text-muted-foreground" />
                        )}
                      </div>
                      <span className={cn(CAPTION, "flex items-center gap-1.5")}>
                        <span>
                          {collection.item_count} {collection.item_count === 1 ? "item" : "items"}
                        </span>
                        {members.length > 0 && (
                          <span className="flex -space-x-1.5">
                            {members.slice(0, 3).map((m) => (
                              <UserAvatar
                                key={m.user_id}
                                avatarUrl={m.avatar_url}
                                name={m.name}
                                username={m.username}
                                size="sm"
                                className="border border-background"
                              />
                            ))}
                            {members.length > 3 && (
                              <span className="flex h-6 w-6 items-center justify-center rounded-full border border-background bg-muted text-ui-badge font-medium text-muted-foreground">
                                +{members.length - 3}
                              </span>
                            )}
                          </span>
                        )}
                        {!isIn && itemVisibility === "private" && collection.visibility === "public" && (
                          <span className="ml-0.5 inline-flex items-center gap-0.5 text-amber-500">
                            Visible to everyone
                          </span>
                        )}
                      </span>
                    </div>

                    {loadingCollectionId === collection.collection_id ? (
                      <Loader2 className="h-5 w-5 shrink-0 animate-spin text-muted-foreground" />
                    ) : isIn ? (
                      <Check className="h-5 w-5 shrink-0 text-primary" />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </DrawerContent>
    </Drawer>

    <PrivacyWarningDialog
      open={privacyWarningCollection !== null}
      onOpenChange={() => setPrivacyWarningCollection(null)}
      title="Share private stash?"
      description={`This stash is private. Anyone can follow "${privacyWarningCollection?.title}" and see it.`}
      onConfirm={() => {
        const coll = privacyWarningCollection;
        setPrivacyWarningCollection(null);
        if (coll) proceedWithAdd(coll);
      }}
    />
    </>
  );
}
